import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Tag, Folder, CheckSquare } from 'lucide-react';
import { Layout } from '../components/layout';
import { Button, Loading, Card } from '../components/common';
import { useReadings, useDeleteReading, useToggleFavorite } from '../hooks/useReadings';
import { useOfflineReadingHistory } from '../hooks/useOfflineReadingHistory';
import { useAdvancedReadingFilters } from '../hooks/useAdvancedReadingFilters';
import { useBulkOperations } from '../hooks/useBulkOperations';
import { loadDeepDives } from '../hooks/useDeepDives';
import AdvancedSearchFilters from '../components/history/AdvancedSearchFilters';
import EnhancedReadingList from '../components/history/EnhancedReadingList';
import ReadingAnalytics from '../components/history/ReadingAnalytics';
import TagManager from '../components/history/TagManager';
import CollectionManager from '../components/history/CollectionManager';
import BulkOperationsBar from '../components/history/BulkOperationsBar';
import OfflineReadingHistoryIndicator from '../components/history/OfflineReadingHistoryIndicator';
import { OfflineAuthGuard } from '../components/auth/OfflineAuthGuard';
import type { TarotReading } from '../types/tarot';
import type { SearchFilters } from '../components/history/AdvancedSearchFilters';
import type { BulkOperation } from '../types/collections';

export default function HistoryPage() {
  const [filters, setFilters] = useState<SearchFilters>({
    query: '',
    dateRange: { start: '', end: '' },
    spreadTypes: [],
    favoriteOnly: false,
    privateOnly: false,
    tags: [],
    sortBy: 'newest',
    sortOrder: 'desc'
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showTagManager, setShowTagManager] = useState(false);
  const [showCollectionManager, setShowCollectionManager] = useState(false);
  
  const { data: readingsData, isLoading, error } = useReadings({ limit: 1000 }); // Fetch all readings
  const deleteReading = useDeleteReading();
  const toggleFavorite = useToggleFavorite();
  
  // Extract readings array from API response
  const readings = readingsData?.data?.readings || [];
  
  // Debug: Log readings data
  console.log('[HistoryPage] Readings data:', {
    hasReadingsData: !!readingsData,
    readingsCount: readings.length,
    totalFromAPI: readingsData?.data?.total,
    pagination: readingsData?.data
  });
  
  // Offline reading history management
  const {
    canAccessOffline,
    accessReason,
    isOfflineMode,
    displayReadings: cachedReadings,
    cacheMetadata,
    isUsingCache,
    isCacheStale,
    refreshCache,
    updateCachedReading,
    removeCachedReading,
    clearCache,
    isLoadingCache,
    cacheError
  } = useOfflineReadingHistory();
  
  // Use online readings if available, otherwise use cached readings
  const finalReadings = isOfflineMode ? cachedReadings : readings;
  
  // Debug: Log final readings selection
  console.log('[HistoryPage] Final readings:', {
    isOfflineMode,
    isUsingCache,
    onlineReadingsCount: readings.length,
    cachedReadingsCount: cachedReadings.length,
    finalReadingsCount: finalReadings.length
  });
  
  // Bulk operations
  const {
    selectedReadings,
    selectedReadingsSet,
    isSelectionMode,
    toggleSelection,
    selectAll,
    selectNone,
    selectFiltered,
    enterSelectionMode,
    exitSelectionMode,
    executeBulkOperation
  } = useBulkOperations();
  
  // Use advanced filtering hook
  const { filteredReadings, availableTags, filterStats } = useAdvancedReadingFilters(
    finalReadings,
    filters
  );
  
  const handleToggleFavorite = async (reading: TarotReading) => {
    try {
      if (isOfflineMode && isUsingCache) {
        // Handle offline favorite toggle
        const success = await updateCachedReading(reading.id, {
          isFavorite: !reading.isFavorite
        });
        if (!success) {
          console.error('Failed to update cached reading');
        }
      } else {
        // Handle online favorite toggle
        await toggleFavorite.mutateAsync({
          id: reading.id,
          isFavorite: !reading.isFavorite
        });
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };
  
  const handleDeleteReading = async (id: string) => {
    try {
      if (isOfflineMode && isUsingCache) {
        // Handle offline deletion
        const success = await removeCachedReading(id);
        if (!success) {
          console.error('Failed to remove cached reading');
        }
      } else {
        // Handle online deletion
        await deleteReading.mutateAsync(id);
      }
    } catch (error) {
      console.error('Failed to delete reading:', error);
    }
  };

  const handleBulkOperation = async (operation: BulkOperation) => {
    await executeBulkOperation(
      operation,
      // onFavorite
      async (readingId: string, isFavorite: boolean) => {
        await toggleFavorite.mutateAsync({ id: readingId, isFavorite });
      },
      // onDelete
      async (readingId: string) => {
        await deleteReading.mutateAsync(readingId);
      },
      // onAddTag - Mock implementation (would integrate with API)
      async (readingId: string, tag: string) => {
        console.log(`Adding tag "${tag}" to reading ${readingId}`);
        // In a real app, this would call an API to add the tag
      },
      // onRemoveTag - Mock implementation
      async (readingId: string, tag: string) => {
        console.log(`Removing tag "${tag}" from reading ${readingId}`);
        // In a real app, this would call an API to remove the tag
      },
      // onAddToCollection - Mock implementation
      async (readingId: string, collectionId: string) => {
        console.log(`Adding reading ${readingId} to collection ${collectionId}`);
        // In a real app, this would call an API to add to collection
      },
      // onRemoveFromCollection - Mock implementation
      async (readingId: string, collectionId: string) => {
        console.log(`Removing reading ${readingId} from collection ${collectionId}`);
        // In a real app, this would call an API to remove from collection
      }
    );
  };
  
  if (isLoading || isLoadingCache) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Loading 
            size="lg" 
            text={isOfflineMode ? "Loading cached reading history..." : "Loading your reading history..."} 
          />
        </div>
      </Layout>
    );
  }
  
  // Handle offline access denied
  if (isOfflineMode && !canAccessOffline) {
    return (
      <OfflineAuthGuard requireAuth={true}>
        <Layout>
          <div className="container mx-auto px-4 py-8">
            <Card className="text-center">
              <h2 className="text-xl font-bold text-white mb-2">
                Offline Access Unavailable
              </h2>
              <p className="text-gray-400 mb-4">
                {accessReason}
              </p>
              <Button onClick={() => window.location.reload()}>
                Check Connection & Retry
              </Button>
            </Card>
          </div>
        </Layout>
      </OfflineAuthGuard>
    );
  }

  if (error && !isOfflineMode) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="text-center">
            <h2 className="text-xl font-bold text-white mb-2">
              Unable to Load History
            </h2>
            <p className="text-gray-400 mb-4">
              There was an error loading your reading history.
            </p>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }
  
  return (
    <OfflineAuthGuard requireAuth={true}>
      <Layout>
        <div className="container mx-auto px-4 py-8">
          {/* Offline Reading History Indicator */}
          <div className="mb-6">
            <OfflineReadingHistoryIndicator
              isOnline={!isOfflineMode}
              isUsingCache={isUsingCache}
              isCacheStale={isCacheStale}
              canAccessOffline={canAccessOffline}
              accessReason={accessReason}
              cacheMetadata={cacheMetadata}
              onRefresh={refreshCache}
              onClearCache={clearCache}
              isRefreshing={isLoading || isLoadingCache}
            />
          </div>

          {/* Header */}
          <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Reading History
              </h1>
              <p className="text-gray-400">
                Review your past tarot readings and insights
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant={showAnalytics ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setShowAnalytics(!showAnalytics)}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </Button>

              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowTagManager(true)}
              >
                <Tag className="h-4 w-4 mr-2" />
                Tags
              </Button>

              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowCollectionManager(true)}
              >
                <Folder className="h-4 w-4 mr-2" />
                Collections
              </Button>

              <Button
                variant="secondary"
                size="sm"
                onClick={enterSelectionMode}
                disabled={finalReadings.length === 0}
              >
                <CheckSquare className="h-4 w-4 mr-2" />
                Select
              </Button>
              
              <Link to="/reading">
                <Button size="sm">
                  + New Reading
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Analytics Section */}
        {showAnalytics && (
          <div className="mb-8">
            <ReadingAnalytics readings={finalReadings} />
          </div>
        )}
        
        {/* Advanced Search and Filters */}
        <AdvancedSearchFilters
          filters={filters}
          onFiltersChange={setFilters}
          availableTags={availableTags}
          isExpanded={filtersExpanded}
          onToggleExpanded={() => setFiltersExpanded(!filtersExpanded)}
        />
        
        {/* Results Summary */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm">
            <div className="text-gray-400">
              <span className="text-white font-medium">{filterStats.filtered}</span>
              {' '}of{' '}
              <span className="text-white font-medium">{filterStats.total}</span>
              {' '}readings
              {filterStats.hasActiveFilters && (
                <span className="ml-2 px-2 py-1 bg-primary-500/20 text-primary-400 text-xs rounded-full">
                  Filtered
                </span>
              )}
            </div>
            
            {filterStats.hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters({
                  query: '',
                  dateRange: { start: '', end: '' },
                  spreadTypes: [],
                  favoriteOnly: false,
                  privateOnly: false,
                  tags: [],
                  sortBy: 'newest',
                  sortOrder: 'desc'
                })}
                className="text-gray-400 hover:text-white"
              >
                Clear All Filters
              </Button>
            )}
          </div>
        </div>
        
        {/* Bulk Operations Bar */}
        <BulkOperationsBar
          readings={readings}
          filteredReadings={filteredReadings}
          selectedReadings={selectedReadings}
          isSelectionMode={isSelectionMode}
          onToggleSelection={toggleSelection}
          onSelectAll={() => selectAll(readings)}
          onSelectNone={selectNone}
          onSelectFiltered={() => selectFiltered(filteredReadings)}
          onExitSelectionMode={exitSelectionMode}
          onExecuteBulkOperation={handleBulkOperation}
        />

        {/* Enhanced Reading List */}
        <EnhancedReadingList
          readings={filteredReadings}
          onToggleFavorite={handleToggleFavorite}
          onDeleteReading={handleDeleteReading}
          isLoading={deleteReading.isPending || toggleFavorite.isPending}
          isSelectionMode={isSelectionMode}
          selectedReadings={selectedReadingsSet}
          onToggleSelection={toggleSelection}
          onLoadDeepDives={loadDeepDives}
        />

        {/* Tag Manager Modal */}
        {showTagManager && (
          <TagManager
            readings={readings}
            isOpen={showTagManager}
            onClose={() => setShowTagManager(false)}
          />
        )}

        {/* Collection Manager Modal */}
        {showCollectionManager && (
          <CollectionManager
            readings={readings}
            isOpen={showCollectionManager}
            onClose={() => setShowCollectionManager(false)}
          />
        )}

        </div>
      </Layout>
    </OfflineAuthGuard>
  );
}